import { PropertyPreservationDTO } from "../DTO/propertyPreservationDTO";

export interface WithVendor {
  vendorId?: number;
}

/**
 * void of an entry (any type): staff see it greyed, clients don't get it, it is not part of reports and its upload links are withdrawn.
 * Stored in the entry's JSON (the server reads "void")
 */
export interface PreservationVoid {
  void?: boolean | null;
  voidReason?: string | null;
  voidedAt?: string | null;
  voidedBy?: string | null;
}

/** form controls of every entry that are no data of the work (void, creation) */
export const PRESERVATION_META_KEYS = ['id', 'preservationType', 'void', 'voidReason', 'voidedAt', 'voidedBy', 'createdAt'];

export type Preservation = WinterizePreservation | GrassPreservation | TrashOutPreservation | BoardUpPreservation | ReKeyPreservation | LockBoxPreservation | NoticePostedPreservation | AlarmPreservation | OngoingInspectionPreservation | RepairPreservation;

export class WinterizePreservation implements WithVendor {
    public winterize!: number; //0=not required, 1=yes, 2=no
    public vendorId?: number;

    public serialize(): any {
        return { ...this};
    }
    public static deserialize(raw: any): WinterizePreservation {
        return Object.assign(new WinterizePreservation(), raw);
    }
}

export class GrassPreservation implements WithVendor {
    public grass: boolean = false;
    public vendorId?: number;

    public serialize(): any {
        return { ...this };
    }
    public static deserialize(raw: any): GrassPreservation {
        return Object.assign(new GrassPreservation(), raw);
    }
}

export class TrashOutPreservation implements WithVendor {
    public vendorId?: number;
    public dateTrashOut?: string;
    public cost?: number;

    public serialize(): any {
        return { ...this };
    }
    public static deserialize(raw: any): TrashOutPreservation {
       return Object.assign(new TrashOutPreservation(), raw);
    }
}

export class BoardUpPreservation implements WithVendor {
    public vendorId?: number;
    public dateBoardUp?: string;
    public cost?: number;

    public serialize(): any {
        return { ...this};
    }
    public static deserialize(raw: any): BoardUpPreservation {
        return Object.assign(new BoardUpPreservation(), raw);
    }
}

export class ReKeyPreservation implements WithVendor {
    public vendorId?: number;
    public dateReKey?: string;
    public cost?: number;

    public serialize(): any {
        return { ...this };
    }
    public static deserialize(raw: any): ReKeyPreservation {
        return Object.assign(new ReKeyPreservation(), raw);
    }
}

export class LockBoxPreservation {
    public code?: string;
    public location?: string;

    public serialize(): any {
        return { ...this };
    }
    public static deserialize(raw: any): LockBoxPreservation {
        return Object.assign(new LockBoxPreservation(), raw);
    }
}

export class NoticePostedPreservation {
    public noticeType?: string;
    public datePosted?: string;
    public location?: string;

    public serialize(): any {
        return { ...this};
    }
    public static deserialize(raw: any): NoticePostedPreservation {
        return Object.assign(new NoticePostedPreservation(), raw);
    }
}

export class AlarmPreservation implements WithVendor {
    public alarm: boolean = false;
    public code?: string;
    public vendorId?: number;

    public serialize(): any {
        return { ...this};
    }
    public static deserialize(raw: any): AlarmPreservation {
        return Object.assign(new AlarmPreservation(), raw);
    }
}

export class OngoingInspectionPreservation implements WithVendor {
    public vendorId?: number;
    public dateInspected?: string;
    public dateNextInspection?: string;
    //public images: number[]; need to deal with images somehow => probably an array of image ids or so
    public serialize(): any {
        return { ...this};
    }
    public static deserialize(raw: any): OngoingInspectionPreservation {
        return Object.assign(new OngoingInspectionPreservation(), raw);
    }
}


export type RepairStatus = 'planned' | 'in_progress' | 'done';

export const REPAIR_STATUS_NAMES: Record<RepairStatus, string> = {
    planned: 'Planned',
    in_progress: 'In progress',
    done: 'Done',
};

/** repair type 'other': no service of the vendors list */
export const REPAIR_TYPE_OTHER = 'other';

/** one repair (several per property): repairType = service code of the vendors list (VendorsServicesService) or 'other' */
export class RepairPreservation implements WithVendor {
    public repairType?: string;
    public description?: string;
    public vendorId?: number;
    public status: RepairStatus = 'planned';
    public dateCompleted?: string; //when done
    public createdAt?: string; //set on first save

    public serialize(): any {
        return { ...this };
    }
    public static deserialize(raw: any): RepairPreservation {
        return Object.assign(new RepairPreservation(), raw);
    }
}

export class PropertyPreservation{
    public id?:number;
    public propertyId!:number;
    public preservationType!:string;  //see map down below
    public preservation!:Preservation;

    constructor(){
    }

    public hasVendor(): boolean {
        return 'vendorId' in this.preservation && this.preservation['vendorId'] !== undefined;
    }

    public static preservationTypeMap: Record<string, new () => Preservation> = {
        'winterize':                WinterizePreservation,
        'grass':                    GrassPreservation,
        'trash_out':                TrashOutPreservation,
        'board_up':                 BoardUpPreservation,
        're_key':                   ReKeyPreservation,
        'lock_box':                 LockBoxPreservation,
        'notice_posted':            NoticePostedPreservation,
        'alarm':                    AlarmPreservation,
        'ongoing_inspection':       OngoingInspectionPreservation,
        'repair':                   RepairPreservation,
    };

    public static preservationNameMap: Record<string, string> = {
        'winterize':                'Winterized',
        'grass':                    'Grass',
        'trash_out':                'Trash Out',
        'board_up':                 'Board Up',
        're_key':                   'Re-Key',
        'lock_box':                 'Lock Box',
        'notice_posted':            'Notices Posted',
        'alarm':                    'Alarm',
        'ongoing_inspection':       'Ongoing Inspections',
        'repair':                   'Repairs',
    };

    public static preservationSingularityMap: Record<string, boolean> = {
        'winterize':                true,
        'grass':                    true,
        'trash_out':                true,
        'board_up':                 true,
        're_key':                   true,
        'lock_box':                 false,
        'notice_posted':            false,
        'alarm':                    true,
        'ongoing_inspection':       true,
        'repair':                   false,
    };

    public static mapToDTO(param: PropertyPreservation): PropertyPreservationDTO {
        ;
        let dto = new PropertyPreservationDTO();
        try {
            dto.id = param.id;
            dto.propertyId = param.propertyId;
            dto.preservationType = param.preservationType;
            const data = (param.preservation as any)?.serialize
                ? (param.preservation as any).serialize()
                : param.preservation;
            dto.preservation = JSON.stringify(data, (key, value) => value ?? undefined);
        } catch (e) {
            console.log(e);
        }
        return dto;
    }

    public static mapFromDTO(param: PropertyPreservationDTO): PropertyPreservation {
        ;
        let obj = new PropertyPreservation();
        try {
            obj.id = param.id;
            obj.propertyId = param.propertyId;
            obj.preservationType = param.preservationType;
            if (param.preservation) {
                const raw = JSON.parse(param.preservation);
                const TaskClass = PropertyPreservation.preservationTypeMap[param.preservationType];
                if (TaskClass) {
                    obj.preservation = (TaskClass as any).deserialize
                        ? (TaskClass as any).deserialize(raw)
                        : Object.assign(new TaskClass(), raw);
                }
            }
        } catch (e) {
            console.log(e);
        }
        return obj;
    }
}

